import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast, toast } from '@/hooks/use-toast';
import { 
  MessageCircle, 
  Heart, 
  Eye, 
  Bell, 
  Coins,
  CreditCard,
  UserPlus,
  ChevronRight,
  Trophy
} from 'lucide-react';
import type { NotificationTypeValue } from '@/types/notification';
import { NotificationType } from '@/types/notification';
import { cn } from '@/lib/utils';
import { resolveMediaUrl } from '@/lib/media';
import { useNotifications } from '@/contexts/NotificationContext';

interface NotificationToastProps {
  id: string;
  type: NotificationTypeValue;
  title: string;
  content: string;
  data?: any;
  characterImage?: string;
  notificationId?: number;
}

export const showNotificationToast = (notification: Omit<NotificationToastProps, 'id'>) => {
  // Erzeuge zunächst einen leeren Toast, um die generierte ID zu erhalten
  const created = toast({
    description: null as unknown as React.ReactNode,
  });

  // Aktualisiere den Toast: rendere die inhaltliche Komponente und übertrage das Styling
  // auf das äußere Toast-Element, damit keine verschachtelten Container entstehen
  created.update({
    id: created.id,
    className: [
      // Visuelles Design, das zuvor auf dem inneren Container lag
      "design-glass backdrop-blur-xl border border-white/10",
      "rounded-token-card shadow-token-modal cursor-pointer",
      "animate-in slide-in-from-right-5 fade-in duration-300",
      "hover:scale-[1.02] transition-all hover:border-white/20",
      "max-w-md w-full",
      "bg-gradient-to-r from-brand-secondary/90 to-brand-secondary-dark/90",
      // Platz für den Close-Button des äußeren Toasts
      "pr-12",
    ].join(" "),
    description: (
      <NotificationToast
        id={created.id}
        {...notification}
      />
    ),
  });

  return created.id;
};

const NotificationToast: React.FC<NotificationToastProps> = ({ 
  id,
  type,
  title,
  content,
  data,
  characterImage,
  notificationId
}) => {
  const navigate = useNavigate();
  const { markAsRead } = useNotifications();
  const { dismiss } = useToast();
  
  const getIcon = () => {
    switch (type) {
      case NotificationType.MESSAGE:
      case NotificationType.FOLLOW_UP:
        return <MessageCircle className="h-5 w-5" />;
      case NotificationType.PROFILE_VISIT:
        return <Eye className="h-5 w-5" />;
      case NotificationType.MATCH:
        return <UserPlus className="h-5 w-5" />;
      case NotificationType.LIKE:
        return <Heart className="h-5 w-5" />;
      case NotificationType.COIN_PURCHASE:
        return <Coins className="h-5 w-5" />;
      case NotificationType.SUBSCRIPTION:
        return <CreditCard className="h-5 w-5" />;
      case NotificationType.ACHIEVEMENT_UNLOCKED:
        return <Trophy className="h-5 w-5" />;
      default:
        return <Bell className="h-5 w-5" />;
    }
  };

  const getIconBgColor = () => {
    switch (type) {
      case NotificationType.MESSAGE:
      case NotificationType.FOLLOW_UP:
        return 'bg-info/20';
      case NotificationType.PROFILE_VISIT:
        return 'bg-brand-secondary/20';
      case NotificationType.MATCH:
        return 'bg-success/20';
      case NotificationType.LIKE:
        return 'bg-brand-primary/20';
      case NotificationType.COIN_PURCHASE:
        return 'bg-warning/20';
      case NotificationType.SUBSCRIPTION:
        return 'bg-brand-secondary/20';
      case NotificationType.ACHIEVEMENT_UNLOCKED:
        return 'bg-warning/20';
      default:
        return 'bg-muted/20';
    }
  };

  const handleClick = async () => {
    // Dismiss nur diesen Toast
    dismiss(id);
    // Mark as read if we have an id
    if (notificationId) {
      try {
        await markAsRead([notificationId]);
      } catch (e) {
        // noop
      }
    }
    
    // Navigate based on notification type
    if (type === NotificationType.PROFILE_VISIT) {
      const isCreator = Boolean(data?.is_creator);
      const creatorId = data?.creator_id;
      const characterId = data?.character_id;

      if (isCreator && creatorId) {
        navigate(`/creator/${creatorId}`);
      } else if (characterId) {
        navigate(`/characters/${characterId}`);
      } else {
        navigate('/profile/visits');
      }
    } else if (data) {
      if (data.session_id && data.character_id) {
        navigate(`/chats/${data.character_id}?session=${data.session_id}`);
      } else if (data.character_id) {
        navigate(`/characters/${data.character_id}`);
      } else if (data.match_id) {
        navigate('/matches');
      } else if (type === NotificationType.COIN_PURCHASE || 
                 type === NotificationType.SUBSCRIPTION) {
        navigate('/billing');
      } else if (type === NotificationType.ACHIEVEMENT_UNLOCKED) {
        navigate('/achievements');
      }
    } else if (type === NotificationType.ACHIEVEMENT_UNLOCKED) {
      navigate('/achievements');
    }
  };

  return (
    <div
      className={cn(
        // Nur inhaltliches Layout – Container/Styling kommt vom äußeren Toast
        "relative flex items-start gap-token-md",
        // Übernimmt die Padding-Konfiguration des äußeren Toasts (p-4, pr-8/pr-12)
        "w-full"
      )}
      onClick={handleClick}
    >
      {/* Icon or Character Image */}
      <div className="shrink-0">
        {characterImage ? (
          <div className="relative w-12 h-12 rounded-full overflow-hidden ring-2 ring-white/20">
            <img
              src={resolveMediaUrl(characterImage)}
              alt=""
              className="w-full h-full object-cover"
            />
            <div className={cn(
              "absolute bottom-0 right-0 w-4 h-4 rounded-full flex items-center justify-center",
              getIconBgColor()
            )}>
              {React.cloneElement(getIcon(), { className: "h-2.5 w-2.5 text-white" })}
            </div>
          </div>
        ) : (
          <div className={cn(
            "w-12 h-12 rounded-full flex items-center justify-center",
            getIconBgColor()
          )}>
            {React.cloneElement(getIcon(), { className: "h-6 w-6 text-white" })}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pr-token-md">
        <h4 className="font-semibold design-text-primary text-sm mb-0.5 truncate">
          {title}
        </h4>
        <p className="design-text-secondary text-sm line-clamp-2">
          {content}
        </p>
      </div>

      {/* Action indicator */}
      <ChevronRight className="h-4 w-4 design-text-secondary shrink-0 mt-token-sm" />
    </div>
  );
};

// Mobile optimized version
export const showMobileNotificationToast = (notification: Omit<NotificationToastProps, 'id'>) => {
  const created = toast({
    description: null as unknown as React.ReactNode,
  });

  created.update({
    id: created.id,
    className: [
      // Mobile: voller Viewport und horizontales Padding
      "w-screen px-token-sm sm:w-auto sm:px-0",
      // Visuelles Design analog Desktop
      "design-glass backdrop-blur-xl border border-white/10",
      "rounded-token-card shadow-token-modal cursor-pointer",
      "animate-in slide-in-from-right-5 fade-in duration-300",
      "hover:scale-[1.02] transition-all hover:border-white/20",
      "max-w-md w-full",
      "bg-gradient-to-r from-brand-secondary/90 to-brand-secondary-dark/90",
      "pr-12",
    ].join(" "),
    description: (
      <NotificationToast
        id={created.id}
        {...notification}
      />
    ),
  });

  return created.id;
};